import React, { useEffect, useState } from "react";
import { Formik, Form, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../Appstore/store";
import { GetSmsCampaignListView } from "../Features/smscampaignlist/Smscampaiglistnview";
import { UpdateSmsCampaignsList } from "../Features/smscampaignlist/smscampaignlist";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useDropzone } from "react-dropzone";
import { setIsLoading } from "../Features/loadingSlice";
import { toast } from "sonner";
import { IoDownloadOutline } from "react-icons/io5";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatDate } from "../helper";

interface FileUploadProps {
  field: any;
  form: any;
  label: string;
}

interface smsCampaignListDatatype {
  name: string;
  numbers: File[];
  message: string;
  schedule_time: string | null;
  retries: number;
  retry_minutes: number;
}

interface FormValues {
  name: string;
  numbers: File[] | null;
  message: string;
  scheduletime: string;
  id?: number;
  is_numbers: boolean;
  retries?: number;
  is_schedule: boolean;
  retry_minutes: number | string;
}

const Updatesmscampaignlist: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { id }: any = useParams();
  const isActive = (path: string) =>
    location.pathname === path ? "active" : "";

  const { smscampaignlistviewsdata } = useSelector(
    (state: RootState) => state.smscampaignlistview
  );
  const record: smsCampaignListDatatype | any = smscampaignlistviewsdata[0];
  const [lastScheduleTime, setLastScheduleTime] = useState(
    record?.schedule_time || ""
  );

  useEffect(() => {
    if (id) {
      dispatch(setIsLoading(true));
      dispatch(GetSmsCampaignListView(id))
        .unwrap()
        .catch((err) => {
          console.error("Error fetching agent monitor:", err);
        })
        .finally(() => {
          setTimeout(() => dispatch(setIsLoading(false)), 500);
        });
    }
  }, [id, dispatch]);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required").trim(),
    is_numbers: Yup.boolean(),
    numbers: Yup.mixed()
      .nullable()
      .when("is_numbers", {
        is: true,
        then: (schema) =>
          Yup.array()
            .min(1, "At least one file is required")
            .test(
              "fileType",
              "Only .xlsx and .xls files are allowed",
              (files) => {
                if (!files || files.length === 0) return false;
                return files.every(
                  (file) =>
                    file &&
                    file.name &&
                    (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))
                );
              }
            ),
      }),
    is_schedule: Yup.boolean(),
    message: Yup.string().required("Message is required").trim(),
    scheduletime: Yup.string().when("is_schedule", {
      is: true,
      then: (schema) => Yup.string().required("Schedule time is required"),
    }),
    retries: Yup.number()
      .required("Retries is required")
      .min(0, "Retries must be at least 0"),
    retry_minutes: Yup.number()
      .min(1)
      .max(60)
      .required()
      .label("Retry minutes"),
  });

  const formatScheduleTimeForInput = (scheduleTime: string) => {
    if (!scheduleTime) return "";
    const utcDate = new Date(scheduleTime);
    const estOffset = -5 * 60 * 60 * 1000;
    const estDate = new Date(utcDate.getTime() + estOffset);
    const isoStringEST = estDate.toISOString();
    return isoStringEST.slice(0, 16);
  };

  const formatDateTimeTo12Hour = (datetimeValue: string) => {
    const [date, time] = datetimeValue.split("T");
    let [hours, minutes] = time.split(":");

    const isPM = Number(hours) >= 12;
    const ampm = isPM ? "PM" : "AM";
    hours = String(Number(hours) % 12 || 12).padStart(2, "0");

    return `${date} ${hours}:${minutes}:00 ${ampm}`;
  };

  const initialValues: FormValues = {
    name: record?.name || "",
    numbers: null,
    is_numbers: false,
    message: record?.message || "",
    scheduletime: formatScheduleTimeForInput(record?.schedule_time || ""),
    retries: record?.retries,
    is_schedule: true,
    retry_minutes: record?.retry_minutes || "",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };


  const FileUpload: React.FC<FileUploadProps> = ({ field, form, label }) => {
    const { setFieldValue, values } = form;

    const onDrop = (acceptedFiles: File[]) => {
      setFieldValue(field.name, acceptedFiles);
    };

    const handleRemoveFile = (index: number) => {
      const newFiles = field.value.filter((_: File, i: number) => i !== index);
      setFieldValue(field.name, newFiles);
    };

    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
        "application/vnd.ms-excel": [".xls"],
      },
      multiple: false,
    });

    return (
      <>
        <label htmlFor="numbers">
          Numbers (Upload Excel File)
          <span className="text-danger">*</span>
        </label>
        <div className="d-flex gap-2 justify-content-center align-items-center form-control ">
          <div {...getRootProps()} className="dropzone flex-grow-1">
            <input {...getInputProps()} />
            <p
              className={`shadow-none mb-0 ${form.touched.numbers && form.errors.numbers ? "is-invalid" : ""
                }`}
            >
              Upload or drop a file right here
            </p>
          </div>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>sample file</Tooltip>}
          >
            <a
              type="button"
              rel="noreferrer"
              href={
                process.env.REACT_APP_BASE_PATH +
                "/assets/sample/SMS-sample-file.xlsx"
              }
              target="_blank"
              download="SMS-sample-file"
            >
              <IoDownloadOutline
                size={22}
                data-bs-title="This top tooltip is themed via CSS variables."
                data-bs-custom-class="custom-tooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
              />
            </a>
          </OverlayTrigger>
        </div>

        {Array.isArray(field.value) && field.value.length > 0 ? (
          <div className="d-flex flex-wrap">
            {field.value.map((file: File | string, index: number) => (
              <div
                key={index}
                className="d-flex align-items-center mb-3 border p-2 mt-2"
              >
                <p className="mb-0">
                  {typeof file === "string" ? file : file.name}
                </p>
                <span className="ms-2" onClick={() => handleRemoveFile(index)}>
                  <i
                    className="fa-solid fa-square-xmark text-danger"
                    style={{ cursor: "pointer" }}
                  ></i>
                </span>
              </div>
            ))}
          </div>
        ) : values.is_numbers === false ? (
          <div className="d-flex flex-wrap">
            <div className="d-flex align-items-center mb-3 border p-2 mt-2">
              <p className="mb-0">{record?.xls_file}</p>
              <span
                className="ms-2"
                onClick={() => {
                  setFieldValue("is_numbers", true);
                  setFieldValue("numbers", []);
                }}
              >
                <i
                  className="fa-solid fa-square-xmark text-danger"
                  style={{ cursor: "pointer" }}
                ></i>
              </span>
            </div>
          </div>
        ) : null}

        {form.touched.numbers && form.errors.numbers && (
          <div className="text-danger">{form.errors.numbers}</div>
        )}
      </>
    );
  };

  return (
    <>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">
                    Update SMS Campaign List
                  </h2>
                  <Link to={""} className="text-decoration-none">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={async (
                      values: FormValues,
                      { setSubmitting }: FormikHelpers<FormValues>
                    ) => {
                      const UpdatedData: smsCampaignListDatatype = {
                        name: values.name,
                        numbers: values.numbers!,
                        message: values.message,
                        schedule_time: values.scheduletime
                          ? formatDateTimeTo12Hour(values.scheduletime)
                          : null,
                        retries: Number(values.retries),
                        retry_minutes: Number(values?.retry_minutes),
                      };

                      try {
                        const response: any = await dispatch(
                          UpdateSmsCampaignsList({
                            sms_campaign_id: record.sms_campaign_id,
                            id: id,
                            updatedData: UpdatedData,
                          })
                        );

                        if (
                          response.payload &&
                          response.payload.status === "success"
                        ) {
                          toast.success(response.payload.message);
                          navigate(
                            `/admin/sms-campaigns/view/sms-campaigns-list/${record?.sms_campaign_id}/list`
                          );
                        } else {
                          toast.error(response.payload.message);
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                      setFieldValue,
                    }) => (
                      <Form className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3 ">
                            <label htmlFor="name">
                              Name
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.name && errors.name ? "is-invalid" : ""
                                }`}
                              id="name"
                              name="name"
                              placeholder="Enter a Name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.name && errors.name && (
                              <div className="text-danger">{errors.name}</div>
                            )}
                          </div>

                          <div className="row">
                            <div className="col-6">
                              <div className="form-group mb-3">
                                <label htmlFor="retries">
                                  Retries<span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  className={`form-control shadow-none ${touched.retries && errors.retries
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  id="retries"
                                  name="retries"
                                  placeholder="Enter a Retries Number"
                                  value={values.retries}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.retries && errors.retries && (
                                  <div className="text-danger">
                                    {errors.retries}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="form-group mb-3 ">
                                <label htmlFor="retry_minutes">
                                  Retry Minutes
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  className={`form-control shadow-none ${touched.retry_minutes &&
                                    errors.retry_minutes
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  id="retry_minutes"
                                  name="retry_minutes"
                                  placeholder="Enter a retry minutes"
                                  value={values.retry_minutes}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.retry_minutes &&
                                  errors.retry_minutes && (
                                    <div className="text-danger">
                                      {errors.retry_minutes}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>

                          <div className="form-group mb-3">
                            <Field name="numbers" component={FileUpload} />
                          </div>
                        </div>
                        <div className="form-group mb-3 col-md-6">
                          <label htmlFor="message">
                            Message<span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control shadow-none ${touched.message && errors.message
                              ? "is-invalid"
                              : ""
                              }`}
                            id="message"
                            name="message"
                            placeholder="Enter a Message"
                            style={{ resize: "none", height: 210 }}
                            value={values.message}
                            onChange={(e) =>
                              setFieldValue(
                                "message",
                                e.target.value?.replaceAll("$", "")
                              )
                            }
                            onBlur={handleBlur}
                          />
                          <small>
                            <b>Note:</b> Use square brackets "[]" to insert the
                            corresponding value from the XLSX file. For example,
                            [Firstname].
                          </small>
                          {touched.message && errors.message && (
                            <div className="text-danger">{errors.message}</div>
                          )}
                        </div>

                        <div className="form-group mb-3 col-md-6">
                          <div className="d-flex gap-2 user-select-none">
                            <input
                              type="checkbox"
                              checked={values.is_schedule}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setFieldValue("is_schedule", isChecked);

                                if (!isChecked) {
                                  setLastScheduleTime(values.scheduletime);
                                  // const now = new Date();
                                  // now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                                  setFieldValue("scheduletime", '');
                                }
                                else {
                                  setFieldValue("scheduletime", lastScheduleTime || "");
                                }
                              }}
                              className="mb-2"
                              id="show_schedule"
                            />
                            <label htmlFor="show_schedule">
                              Schedule Campaign ?
                            </label>
                          </div>

                          {values.is_schedule ? (
                            <input
                              type="datetime-local"
                              className={`form-control shadow-none ${touched.scheduletime && errors.scheduletime
                                ? "is-invalid"
                                : ""
                                }`}
                              id="scheduletime"
                              name="scheduletime"
                              value={values.scheduletime}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          ) : null}

                          {touched.scheduletime && errors.scheduletime && (
                            <div className="text-danger">
                              {errors.scheduletime}
                            </div>
                          )}
                        </div>

                        <div className="d-flex">
                          <button
                            type="submit"
                            className="btn btn-primary me-2"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ps-2">Submitting...</span>
                              </>
                            ) : (
                              "Submit"
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Updatesmscampaignlist;
